import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom-v5-compat';

import { Shell } from './Shell';
import TicketsGate from '../features/tickets/TicketsGate';
import HrGate from '../features/hr/HrGate';
import CredentialsGate from '../features/credentials/CredentialsGate';
import InventoryGate from '../features/inventory/InventoryGate';

const PosPage = lazy(() => import('../features/pos/PosPage'));

const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));

const ProductsPage = lazy(() => import('../features/products/ProductsPage'));
const ProductCreatePage = lazy(() => import('../features/products/ProductCreatePage'));
const ProductEditPage = lazy(() => import('../features/products/ProductEditPage'));
const ProductViewPage = lazy(() => import('../features/products/ProductViewPage'));
const ProductImportPage = lazy(() => import('../features/products/ProductImportPage'));
const SinglesWizardPage = lazy(() => import('../features/singlesWizard/SinglesWizardPage'));
const SinglesExpansionsPage = lazy(
    () => import('../features/singlesExpansions/SinglesExpansionsPage'),
);
const SinglesExpansionCreatePage = lazy(
    () => import('../features/singlesExpansions/SinglesExpansionCreatePage'),
);
const SinglesExpansionEditPage = lazy(
    () => import('../features/singlesExpansions/SinglesExpansionEditPage'),
);
const SinglesExpansionViewPage = lazy(
    () => import('../features/singlesExpansions/SinglesExpansionViewPage'),
);
const SinglesCardViewPage = lazy(
    () => import('../features/singlesExpansions/SinglesCardViewPage'),
);
const SinglesGamesPage = lazy(() => import('../features/singlesGames/SinglesGamesPage'));
const SinglesControlPanelPage = lazy(
    () => import('../features/singlesControlPanel/SinglesControlPanelPage'),
);
const LabelMakerPage = lazy(() => import('../features/labelMaker/LabelMakerPage'));
const SinglesBrowsePage = lazy(() => import('../features/singlesBrowse/SinglesBrowsePage'));
const TagsPage = lazy(() => import('../features/tags/TagsPage'));
const JobsPage = lazy(() => import('../features/jobs/JobsPage'));
const SinglesJobsPage = lazy(() => import('../features/singlesJobs/SinglesJobsPage'));
const UserTagsPage = lazy(() => import('../features/userTags/UserTagsPage'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const UserCreatePage = lazy(() => import('../features/users/UserCreatePage'));
const UserViewPage = lazy(() => import('../features/users/UserViewPage'));
const UserEditPage = lazy(() => import('../features/users/UserEditPage'));
const PriceListsPage = lazy(() => import('../features/priceLists/PriceListsPage'));
const PreviewAudiencePage = lazy(() => import('../features/preview/PreviewAudiencePage'));
const PriceAnomaliesPage = lazy(
    () => import('../features/priceAnomalies/PriceAnomaliesPage'),
);
const OrderFlagsPage = lazy(() => import('../features/orderFlags/OrderFlagsPage'));
const OrdersPage = lazy(() => import('../features/orders/OrdersPage'));
const OrderCreatePage = lazy(() => import('../features/orders/OrderCreatePage'));
const OrderEditPage = lazy(() => import('../features/orders/OrderEditPage'));
const OrderViewPage = lazy(() => import('../features/orders/OrderViewPage'));
const ShortUrlsPage = lazy(() => import('../features/shortUrls/ShortUrlsPage'));
const CustomerEmailsPage = lazy(() => import('../features/customerEmails/CustomerEmailsPage'));
const DayReportsPage = lazy(() => import('../features/dayReports/DayReportsPage'));
const DayReportCreatePage = lazy(
    () => import('../features/dayReports/DayReportCreatePage'),
);
const ShippingDebugPage = lazy(() => import('../features/shippingDebug/ShippingDebugPage'));
const EmailDebugPage = lazy(() => import('../features/emailDebug/EmailDebugPage'));
const PushPage = lazy(() => import('../features/push/PushPage'));
const GiveawaysPage = lazy(() => import('../features/giveaways/GiveawaysPage'));
const AnalyticsPage = lazy(() => import('../features/analytics/AnalyticsPage'));
const CalendarPage = lazy(() => import('../features/calendar/CalendarPage'));
const CurrentEventsPage = lazy(() => import('../features/currentEvents/CurrentEventsPage'));
const ReservationsPage = lazy(() => import('../features/reservations/ReservationsPage'));
const InvoicesPage = lazy(() => import('../features/invoices/InvoicesPage'));
const GeneralReportPage = lazy(() => import('../features/reports/GeneralReportPage'));
const PosReportPage = lazy(() => import('../features/reports/PosReportPage'));
const EventsReportPage = lazy(() => import('../features/reports/EventsReportPage'));
const CustomersReportPage = lazy(() => import('../features/reports/CustomersReportPage'));
const InvoiceViewPage = lazy(() => import('../features/invoices/InvoiceViewPage'));
const WithdrawalsPage = lazy(() => import('../features/withdrawals/WithdrawalsPage'));
const VacationsPage = lazy(() => import('../features/hr/VacationsPage'));
const CredentialsPage = lazy(() => import('../features/credentials/CredentialsPage'));
const HrShopsPage = lazy(() => import('../features/hr/ShopsPage'));
const TicketsPage = lazy(() => import('../features/tickets/TicketsPage'));
const TicketDetailPage = lazy(() => import('../features/tickets/TicketDetailPage'));
const TicketArchivePage = lazy(() => import('../features/tickets/TicketArchivePage'));
const TicketBacklogPage = lazy(() => import('../features/tickets/TicketBacklogPage'));
const TicketRecurrencesPage = lazy(() => import('../features/tickets/TicketRecurrencesPage'));
const TicketPosterPage = lazy(() => import('../features/tickets/TicketPosterPage'));
const TransfersPage = lazy(() => import('../features/inventory/TransfersPage'));
const TransferPage = lazy(() => import('../features/inventory/TransferPage'));

export default function V2Root() {
    return (
        <Routes>
            {/* Chromeless POS — sibling of Shell so it renders without sidebar/header. */}
            <Route
                path="/v2/pos"
                element={
                    <Suspense
                        fallback={
                            <div className="flex h-screen w-screen items-center justify-center text-sm text-gray-500">
                                Se incarca...
                            </div>
                        }
                    >
                        <PosPage />
                    </Suspense>
                }
            />
            {/* Chromeless print poster (shop QR) — sibling of Shell so Ctrl+P
                prints just the poster, no sidebar/header. Gates its own role. */}
            <Route
                path="/v2/tickets/poster"
                element={
                    <Suspense
                        fallback={
                            <div className="flex h-screen w-screen items-center justify-center text-sm text-gray-500">
                                Se incarca...
                            </div>
                        }
                    >
                        <TicketPosterPage />
                    </Suspense>
                }
            />
            {/* The v2 dashboard is also the app home: served directly at "/"
                (mounted from the v5 switch in modules/index.js, ahead of the
                legacy OrchestraLayout). */}
            <Route path="/" element={<Shell />}>
                <Route index element={<DashboardPage />} />
            </Route>
            <Route path="/v2" element={<Shell />}>
                <Route index element={<Navigate to="/v2/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/create" element={<ProductCreatePage />} />
                {/* Static segment BEFORE products/:id so /import is never
                    captured as an id (same ordering as orders/create). */}
                <Route path="products/import" element={<ProductImportPage />} />
                <Route path="products/:id" element={<ProductViewPage />} />
                <Route path="products/:id/edit" element={<ProductEditPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="singles/cards" element={<SinglesBrowsePage />} />
                <Route path="singles/singles-wizard" element={<SinglesWizardPage />} />
                <Route path="singles/singles-expansions" element={<SinglesExpansionsPage />} />
                <Route
                    path="singles/singles-expansions/create"
                    element={<SinglesExpansionCreatePage />}
                />
                <Route
                    path="singles/singles-expansions/:id"
                    element={<SinglesExpansionViewPage />}
                />
                <Route
                    path="singles/singles-expansions/:id/edit"
                    element={<SinglesExpansionEditPage />}
                />
                <Route path="singles/cards/:id" element={<SinglesCardViewPage />} />
                <Route path="singles/label-maker" element={<LabelMakerPage />} />
                <Route path="singles/singles-games" element={<SinglesGamesPage />} />
                <Route path="singles/control-panel" element={<SinglesControlPanelPage />} />
                <Route path="singles/operations" element={<SinglesJobsPage />} />
                <Route path="jobs" element={<JobsPage />} />
                <Route path="user-tags" element={<UserTagsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/create" element={<UserCreatePage />} />
                <Route path="users/:id" element={<UserViewPage />} />
                <Route path="users/:id/edit" element={<UserEditPage />} />
                <Route path="price-lists" element={<PriceListsPage />} />
                <Route path="preview" element={<PreviewAudiencePage />} />
                <Route path="price-anomalies" element={<PriceAnomaliesPage />} />
                <Route path="order-flags" element={<OrderFlagsPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/create" element={<OrderCreatePage />} />
                <Route path="orders/:id" element={<OrderViewPage />} />
                <Route path="orders/:id/edit" element={<OrderEditPage />} />
                <Route path="short-urls" element={<ShortUrlsPage />} />
                <Route path="customer-emails" element={<CustomerEmailsPage />} />
                <Route path="day-reports" element={<DayReportsPage />} />
                <Route path="day-reports/create" element={<DayReportCreatePage />} />
                <Route path="shipping-debug" element={<ShippingDebugPage />} />
                <Route path="email-debug" element={<EmailDebugPage />} />
                <Route path="push" element={<PushPage />} />
                <Route path="giveaways" element={<GiveawaysPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="current-events" element={<CurrentEventsPage />} />
                <Route path="reservations" element={<ReservationsPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:id" element={<InvoiceViewPage />} />
                <Route path="reports/general" element={<GeneralReportPage />} />
                <Route path="reports/pos" element={<PosReportPage />} />
                <Route path="reports/events" element={<EventsReportPage />} />
                <Route path="reports/customers" element={<CustomersReportPage />} />
                <Route path="withdrawals" element={<WithdrawalsPage />} />
                {/* Echipa (HR) — gated to manager+ (Staff/Manager/Admin), with a
                    loader while the role hydrates so refreshes don't bounce. */}
                <Route path="hr" element={<HrGate />}>
                    <Route path="vacations" element={<VacationsPage />} />
                    <Route path="shops" element={<HrShopsPage />} />
                </Route>
                {/* Inventar — manager+ gate; stock transfers between gestiuni. */}
                <Route path="inventory" element={<InventoryGate />}>
                    <Route index element={<Navigate to="/v2/inventory/transfers" replace />} />
                    <Route path="transfers" element={<TransfersPage />} />
                    <Route path="transfers/:id" element={<TransferPage />} />
                </Route>
                {/* Credentiale — team-level gate; the API scopes rows per-grant. */}
                <Route path="credentials" element={<CredentialsGate />}>
                    <Route index element={<CredentialsPage />} />
                </Route>
                {/* Tickete — inside the v2 Shell; TicketsGate withholds until the
                    role loads and blocks below manager level. */}
                <Route path="tickets" element={<TicketsGate />}>
                    <Route index element={<TicketsPage />} />
                    <Route path="archive" element={<TicketArchivePage />} />
                    <Route path="backlog" element={<TicketBacklogPage />} />
                    <Route path="recurrences" element={<TicketRecurrencesPage />} />
                    <Route path=":id" element={<TicketDetailPage />} />
                </Route>
                <Route
                    path="*"
                    element={
                        <div className="px-4 py-12 text-center text-sm text-gray-500">
                            Pagina nu a fost gasita.
                        </div>
                    }
                />
            </Route>
        </Routes>
    );
}
